/**
 * Event lifecycle — the single source of truth for upcoming → live → over.
 *
 * Liveness comes from the backend Gigcasters probe (streamState: live|vod|down).
 * Per BKFC Tech, a finished live stream's DVR stays playable, so the backend
 * reports `vod` once an event is over (not `down`). `down` therefore means "no
 * playable content yet" — essentially the pre-encoder / delayed-start window.
 *
 *   effectiveStart = start_time ?? event_date
 *   endBoundary    = end_time ?? (effectiveStart + 4h)
 */

export const LifecycleState = {
  BUY_NOW: 'buy-now',
  LIVE: 'live',
  REPLAY: 'replay',
  WATCH_NOW: 'watch-now', // hide_countdown manual override
  UPCOMING: 'upcoming',
  STARTING_SOON: 'starting-soon',
  ENDED: 'ended',
};

export const PRE_START_WINDOW_MS = 60 * 60 * 1000; // start probing/polling 1h before start
export const DEFAULT_EVENT_DURATION_MS = 4 * 60 * 60 * 1000; // endBoundary when end_time unset
export const EVENT_END_GRACE_MS = 30 * 60 * 1000; // ENDED only after end + grace
export const POLL_ACTIVE_MS = 30 * 1000; // upcoming-within-1h + starting-soon
export const POLL_LIVE_MS = 60 * 1000; // slow re-poll while live (catch drops)
export const GATE_CHECK_MS = 60 * 1000; // local-only threshold check when >1h out

export function computeEndBoundary(effectiveStartMs, endTimeMs) {
  if (endTimeMs != null) return endTimeMs;
  if (effectiveStartMs != null) return effectiveStartMs + DEFAULT_EVENT_DURATION_MS;
  return null;
}

/**
 * Derive the lifecycle state. Inputs are epoch-ms numbers (or null) + the backend
 * streamState ('live' | 'vod' | 'down' | null/unknown).
 */
export function deriveEventLifecycle({
  effectiveStartMs,
  endBoundaryMs,
  streamState,
  hideCountdown,
  isLocked = false,
  now = Date.now(),
}) {
  if (isLocked) return LifecycleState.BUY_NOW;
  if (streamState === 'live') return LifecycleState.LIVE;
  if (streamState === 'vod') return LifecycleState.REPLAY;
  if (hideCountdown) return LifecycleState.WATCH_NOW;
  // stream down / unknown
  if (effectiveStartMs != null && now < effectiveStartMs) return LifecycleState.UPCOMING;
  if (endBoundaryMs != null && now > endBoundaryMs + EVENT_END_GRACE_MS)
    return LifecycleState.ENDED;
  return LifecycleState.STARTING_SOON;
}

/**
 * How the client should poll /events/live-status:
 *   'slow'   — 60s (live; catch a mid-stream drop)
 *   'active' — 30s (within 1h of start, or starting-soon)
 *   'gate'   — no network; 60s local timer waits for the 1h threshold
 *   'stop'   — don't poll (replay is stable, event ended, or no start known)
 */
export function computePollMode({
  effectiveStartMs,
  endBoundaryMs,
  streamState,
  now = Date.now(),
}) {
  if (streamState === 'vod') return 'stop';
  if (streamState === 'live') return 'slow';
  // down / unknown
  if (endBoundaryMs != null && now > endBoundaryMs + EVENT_END_GRACE_MS) return 'stop';
  if (effectiveStartMs == null) return 'stop';
  if (now >= effectiveStartMs - PRE_START_WINDOW_MS) return 'active';
  return 'gate';
}
